import React from "react";
import Container from "react-bootstrap/Container";
import Nav from "react-bootstrap/Nav";
import Navbar from "react-bootstrap/Navbar";
import "../styles/NavBar.css";
import { gsap } from "gsap";

class NavBar extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      show: true,
      lastScrollY: window.scrollY,
      atTop: window.scrollY === 0,
      mobileMenuOpen: false
    };
    this.handleScroll = this.handleScroll.bind(this);
    this.toggleMobileMenu = this.toggleMobileMenu.bind(this);
    this.mobileMenuRef = React.createRef();
    this.hamburgerRef = React.createRef();
    this.navbarRef = React.createRef();
    this.navLinksRef = [];
    this.mobileNavLinksRef = [];
    this.addToDesktopNavRefs = this.addToDesktopNavRefs.bind(this);
    this.addToMobileNavRefs = this.addToMobileNavRefs.bind(this);
    this.logoRef = React.createRef();
    this.initialRenderComplete = false;
    this.scrollSynced = false;

  }

  addToDesktopNavRefs(el) {
    if (el && !this.navLinksRef.includes(el)) {
      this.navLinksRef.push(el);
    }
  }

  addToMobileNavRefs(el) {
    if (el && !this.mobileNavLinksRef.includes(el)) {
      this.mobileNavLinksRef.push(el);
    }
  }

  toggleMobileMenu() {
    const { mobileMenuOpen } = this.state;

    this.setState({ mobileMenuOpen: !mobileMenuOpen });

    if (!mobileMenuOpen) {
      gsap.to(this.mobileMenuRef.current, { x: 0, duration: 0.4, ease: "power3.out" });
      gsap.to(this.hamburgerRef.current.querySelector('.line-1'), { rotation: 45, y: 8, duration: 0.3 });
      gsap.to(this.hamburgerRef.current.querySelector('.line-2'), { opacity: 0, duration: 0.3 });
      gsap.to(this.hamburgerRef.current.querySelector('.line-3'), { rotation: -45, y: -8, duration: 0.3 });

      gsap.set(this.mobileNavLinksRef, {
        autoAlpha: 0,
        y: -15,
        x: -10
      });

      gsap.to(this.mobileNavLinksRef, {
        autoAlpha: 1,
        y: 0,
        x: 0,
        duration: 0.6,
        stagger: 0.12,
        ease: "back.out(1.2)",
        delay: 0.2
      });
    } else {
      gsap.to(this.mobileMenuRef.current, { x: '100%', duration: 0.4, ease: "power3.in" });
      gsap.to(this.hamburgerRef.current.querySelector('.line-1'), { rotation: 0, y: 0, duration: 0.3 });
      gsap.to(this.hamburgerRef.current.querySelector('.line-2'), { opacity: 1, duration: 0.3 });
      gsap.to(this.hamburgerRef.current.querySelector('.line-3'), { rotation: 0, y: 0, duration: 0.3 });
    }
  }

  componentDidMount() {
    window.addEventListener("scroll", this.handleScroll, { passive: true });

    // Set initial state of mobile menu
    gsap.set(this.mobileMenuRef.current, { x: '100%' });

    // Preload animations before first frame
    if (this.navbarRef.current) {
      gsap.set(this.navbarRef.current, { opacity: 0 });
      gsap.set(this.navLinksRef, { autoAlpha: 0, y: -10, x: -5 });
      gsap.set(this.logoRef.current, { autoAlpha: 0 });
      gsap.set('.social-media-btn', { autoAlpha: 0, y: -10 });
    }

    // Use a single timeline for better performance and synchronization
    const tl = gsap.timeline();

    // Show navbar immediately with animation
    tl.to(this.navbarRef.current, {
      opacity: 1,
      duration: 0.4,
      ease: "power2.out"
    }, 0);

    // Animate logo quickly
    tl.to(this.logoRef.current, {
      autoAlpha: 1,
      duration: 0.3,
      ease: "back.out(1.2)"
    }, 0.1);

    // Animate nav links with small stagger
    tl.to(this.navLinksRef, {
      autoAlpha: 1,
      y: 0,
      x: 0,
      duration: 0.3,
      stagger: 0.05,
      ease: "back.out(1.2)"
    }, 0.2);

    // Animate social media buttons
    tl.to('.social-media-btn', {
      autoAlpha: 1,
      y: 0,
      duration: 0.3,
      stagger: 0.05,
      ease: "back.out(1.2)"
    }, 0.3);
  }

  componentWillUnmount() {
    window.removeEventListener("scroll", this.handleScroll);
  }

  handleScroll() {
    const { lastScrollY } = this.state;
    const currentScrollY = window.scrollY;
    const atTop = currentScrollY === 0;

    // Browsers restore the previous scroll offset after first paint. That
    // arrives as one large downward jump, which would otherwise read as the
    // user scrolling down and hide the navbar right after it faded in.
    if (!this.scrollSynced) {
      this.scrollSynced = true;
      this.setState({ show: true, lastScrollY: currentScrollY, atTop });
      return;
    }

    if (currentScrollY > lastScrollY && currentScrollY > 60) {
      this.setState({ show: false, lastScrollY: currentScrollY, atTop });
    } else {
      this.setState({ show: true, lastScrollY: currentScrollY, atTop });
    }
  }

  render() {
    const { show, atTop, mobileMenuOpen } = this.state;

    return (
      <Navbar
        as="nav"
        ref={this.navbarRef}
        fixed="top"
        expand="lg"
        className={`bg-body-tertiary navbar-animated${show ? " navbar-visible" : " navbar-hidden"}${atTop ? " navbar-at-top" : ""}`}
        style={{ zIndex: 1000 }}
      >
        <Container fluid style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <Navbar.Brand href="Home">
            <div className="logo-container btn-effect" ref={this.logoRef}>
              <img
                className="logo"
                src="/assets/logo.png"
                alt="Faizaan Fareed Logo"
                title="Logo"
                style={{ height: "36px", width: "36px" }}
              />
            </div>
          </Navbar.Brand>

          <div className="desktop-nav">
            <Navbar.Collapse id="basic-navbar-nav">
              <Nav className="me-auto">
                <Nav.Link href="#services" ref={this.addToDesktopNavRefs}>Services</Nav.Link>
                <Nav.Link href="#projects" ref={this.addToDesktopNavRefs}>Projects</Nav.Link>
                <Nav.Link href="#about" ref={this.addToDesktopNavRefs}>About</Nav.Link>
                <Nav.Link href="#pricing" ref={this.addToDesktopNavRefs}>Pricing</Nav.Link>
                <Nav.Link href="#faq" ref={this.addToDesktopNavRefs}>FAQ</Nav.Link>
                <Nav.Link href="#contact" ref={this.addToDesktopNavRefs}>Contact</Nav.Link>
              </Nav>
            </Navbar.Collapse>
          </div>

          <div className="mobile-controls">
            <button
              className="hamburger-menu"
              ref={this.hamburgerRef}
              onClick={this.toggleMobileMenu}
            >
              <span className="line line-1"></span>
              <span className="line line-2"></span>
              <span className="line line-3"></span>
            </button>
          </div>

          <div
            className={`mobile-menu${mobileMenuOpen ? " open" : ""}`}
            ref={this.mobileMenuRef}
          >
            <Nav className="mobile-nav-links">
              <Nav.Link href="#services" onClick={this.toggleMobileMenu} ref={this.addToMobileNavRefs}>Services</Nav.Link>
              <Nav.Link href="#projects" onClick={this.toggleMobileMenu} ref={this.addToMobileNavRefs}>Projects</Nav.Link>
              <Nav.Link href="#about" onClick={this.toggleMobileMenu} ref={this.addToMobileNavRefs}>About</Nav.Link>
              <Nav.Link href="#pricing" onClick={this.toggleMobileMenu} ref={this.addToMobileNavRefs}>Pricing</Nav.Link>
              <Nav.Link href="#faq" onClick={this.toggleMobileMenu} ref={this.addToMobileNavRefs}>FAQ</Nav.Link>
              <Nav.Link href="#contact" onClick={this.toggleMobileMenu} ref={this.addToMobileNavRefs}>Contact</Nav.Link>
            </Nav>
          </div>
        </Container>
      </Navbar>
    );
  }
}

export default NavBar;